import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { Radio } from '@openx/components-core/lib/Radio/Radio.tsx';
import type { DspRestriction } from '@openx/types/gqlTypes.ts';
import { dspRestrictionModes } from '@openx/types/organization.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationContext } from '../../../utils/context/OrganizationContext.tsx';
import { DspSeatIdsReadOnly } from './DspSeatIdsReadOnly.tsx';

const modeLabelKeys = {
  [dspRestrictionModes.ALLOW]: 'Allow List',
  [dspRestrictionModes.BLOCK]: 'Block List',
};

type DspRestrictionReadOnlySectionProps = {
  restrictions: DspRestriction[];
  mode?: string | null;
};

export const DspRestrictionReadOnlySection = ({ restrictions, mode }: DspRestrictionReadOnlySectionProps) => {
  const { t } = useTranslation();

  const { useDspsFetch } = useOrganizationContext();
  const { mapById: dspOptions, loading: dspLoading } = useDspsFetch();

  if (dspLoading) {
    return <Loader />;
  }

  return (
    <div data-test="dsp-configuration-section">
      <Grid
        size={12}
        sx={{
          mb: 0.5,
          mt: 2,
        }}
      >
        <Typography variant="body1Bold" data-test="dsp-seats-label" sx={{ textTransform: 'uppercase' }}>
          {t('DSP Configuration')}
        </Typography>
      </Grid>

      {!!mode && (
        <FormControlLabel
          checked
          control={<Radio readOnly />}
          disabled
          label={t(modeLabelKeys[mode])}
          sx={{ marginBottom: 2 }}
        />
      )}

      {restrictions.map(restriction => {
        return (
          <React.Fragment key={restriction.dsp_id}>
            <FieldDetails
              title={t('DSP')}
              value={
                <span data-test="dsp-restriction-value">
                  {dspOptions[restriction.dsp_id]?.name || restriction.dsp_id}
                </span>
              }
            />
            <DspSeatIdsReadOnly dspId={restriction.dsp_id} seatIds={restriction.seat_ids} />
          </React.Fragment>
        );
      })}
    </div>
  );
};
